import type { VesselPlanningSaleOrderBulkDatesRequest } from '../../vessel-planning/api/vesselPlanningApi';
import type { VesselChangeJob } from '../types/vesselChanges';

/** A job appears once per leg, so the row key carries both. */
export const rowKey = (job: Pick<VesselChangeJob, 'saleOrderId' | 'leg'>): string => `${job.saleOrderId}-${job.leg}`;

/** Boarding pay would move to another month: the row waits for "I understand". */
export const needsBoardingConfirm = (job: VesselChangeJob): boolean =>
  Boolean(job.boardingMonthFrom || job.boardingMonthTo);

export interface DateUpdatePlan {
  request: VesselPlanningSaleOrderBulkDatesRequest;
  jobs: VesselChangeJob[];
}

/**
 * One bulk update per leg type and PTP time pair, carrying only that leg's ETA and ETD
 * (loading `eta`/`etd`, off `oeta`/`oetd`) as PTP gives them. A time PTP does not have is left
 * out, so the job keeps its own. Jobs where PTP has neither time are not sent.
 */
export const planDateUpdates = (companyId: number, jobs: VesselChangeJob[]): DateUpdatePlan[] => {
  const plans = new Map<string, DateUpdatePlan>();
  jobs.forEach((job) => {
    if (!job.ptpEta && !job.ptpEtd) return;
    const key = `${job.leg}|${job.ptpEta ?? ''}|${job.ptpEtd ?? ''}`;
    let plan = plans.get(key);
    if (!plan) {
      const [etaKey, etdKey] = job.leg === 'OFF' ? (['oeta', 'oetd'] as const) : (['eta', 'etd'] as const);
      const request: VesselPlanningSaleOrderBulkDatesRequest = { companyId, saleOrderIds: [] };
      if (job.ptpEta) request[etaKey] = job.ptpEta;
      if (job.ptpEtd) request[etdKey] = job.ptpEtd;
      plan = { request, jobs: [] };
      plans.set(key, plan);
    }
    if (!plan.request.saleOrderIds.includes(job.saleOrderId)) plan.request.saleOrderIds.push(job.saleOrderId);
    plan.jobs.push(job);
  });
  return [...plans.values()];
};
